import React from 'react';
import { ChevronRight, FileText, Mic, MicOff, Paperclip, Volume2, VolumeX } from 'lucide-react';
import DocumentUpload from '../DocumentUpload.tsx';

interface Question {
  id: string;
  question: string;
  answer?: string;
  isCollapsed?: boolean;
  field?: string;
  type?: string;
}

interface ElevenLabsService {
  speak: (text: string, options?: any) => Promise<void>;
  stop: () => void;
}

interface QuestionDisplayProps {
  currentQuestion: Question;
  isRecording: boolean;
  showDocumentUpload: boolean;
  uploadedDocuments: { [questionId: string]: File[] };
  elevenLabsError: string | null;
  onAnswerChange: (questionId: string, answer: string) => void;
  onToggleRecording: () => void;
  onToggleDocumentUpload: () => void;
  onDocumentUpload: (questionId: string, file: File) => void;
  onDocumentRemove: (questionId: string, fileIndex: number) => void;
  elevenLabsIsPlaying?: boolean;
  onMuteToggle?: () => void;
  elevenLabs: ElevenLabsService;
  onNextQuestion?: () => void;
  loadingNextQuestion?: boolean;
  isLoadingQuestionWithAudio?: boolean;
  interviewComplete?: boolean;
  isFollowup?: boolean;
  onGenerateSummary?: () => void;
}

const QuestionDisplay: React.FC<QuestionDisplayProps> = ({
  currentQuestion,
  isRecording,
  showDocumentUpload,
  uploadedDocuments,
  elevenLabsError,
  onAnswerChange,
  onToggleRecording,
  onToggleDocumentUpload,
  onDocumentUpload,
  onDocumentRemove,
  elevenLabsIsPlaying,
  elevenLabs,
  onMuteToggle,
  onNextQuestion,
  loadingNextQuestion,
  isLoadingQuestionWithAudio,
  interviewComplete,
  isFollowup,
  onGenerateSummary

}) => {
  const togglePlay = async (questionText: string) => {
    if (elevenLabsIsPlaying) {
      elevenLabs.stop();
    
    } else {
      try {
        await elevenLabs.speak(questionText, {
          stability: 0.5,
          similarityBoost: 0.8,
          style: 0.2,
          useSpeakerBoost: true
        });
        
      } catch (error) {
        console.error('Error playing audio:', error);
      }
    }
  };

  return (
    <div className="mb-10">
      <div className="bg-gradient-to-r from-blue-50 to-blue-100 border-2 border-blue-200 rounded-xl p-4 mb-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xl font-semibold text-blue-900">
            Current Question
          </div>
          {onMuteToggle && (
            <button
              onClick={() => togglePlay(currentQuestion?.question || '')}
              className="inline-flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-md"
              title={elevenLabsIsPlaying ? "Stop Audio" : "Play Audio"}
            >
              {elevenLabsIsPlaying ? (
                <>
                  <VolumeX className="w-4 h-4" />
                </>
              ) : (
                <>
                  <Volume2 className="w-4 h-4" />
                </>
              )}
            </button>
          )}
        </div>
        <p className="text-blue-800 text-lg leading-relaxed">{currentQuestion?.question}</p>
        
      
      </div>

      {/* Answer Input */}
      <div className="space-y-6">
        <div>
          <label className="block text-lg font-semibold text-gray-700 mb-2">
            Your Response (Text)
          </label>
          <textarea
            value={currentQuestion?.answer || ""}
            onChange={(e) => onAnswerChange(currentQuestion.id, e.target.value)}
            rows={4}
            className="w-full px-6 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:border-purple-500 text-lg leading-relaxed"
            style={
              {
                "--tw-ring-color": "#bb1ccc",
              } as React.CSSProperties
            }
            placeholder="Type your response here or use voice input below..."
          />
        </div>

        {/* Speech Recognition */}
        <div className="flex items-center gap-4  items-center justify-between">

          <div  className="flex items-center gap-4">

         

          <button
              onClick={onToggleRecording}
              style={{
                background: isRecording ? "#dc2626" : "linear-gradient(135deg, #bb1ccc 0%, #d946ef 100%)",
              }}
              className="inline-flex items-center gap-3 px-6 py-3 rounded-xl font-semibold transition-all duration-200 text-white hover:opacity-90 transform hover:scale-105 shadow-md"
            >
              {isRecording ? (
                <MicOff className="w-5 h-5" />
              ) : (
                <Mic className="w-5 h-5" />
              )}
              {isRecording ? "Stop Voice Input" : "Start Voice Input"}
            </button>

          {isRecording && (
            <div className="flex items-center gap-3 text-red-600">
              <div className="w-3 h-3 bg-red-600 rounded-full animate-pulse"></div>
              <span className="font-semibold">
                Listening... Speak now
              </span>
            </div>
          )}

          </div>

          <div>
          {isFollowup && !interviewComplete ? (
              <button
                onClick={onNextQuestion}
                disabled={
                  loadingNextQuestion || !currentQuestion?.answer?.trim() || isLoadingQuestionWithAudio
                }
                style={{
                  background: loadingNextQuestion ? "#bb1ccc" : "linear-gradient(135deg, #bb1ccc 0%, #d946ef 100%)",
                }}
                className="inline-flex items-center gap-3 px-8 py-3 text-white rounded-xl font-semibold hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg transform hover:scale-105 disabled:transform-none"
              >
                {(loadingNextQuestion || isLoadingQuestionWithAudio) ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Submitting...
                  </>
                ) : (
                  <>
                    Next Question
                    <ChevronRight className="w-5 h-5" />
                  </>
                )}
              </button>
            ) : (
              <button
                onClick={onGenerateSummary}
                style={{ background: "#22c55e" }}
                className="inline-flex items-center gap-3 px-8 py-4 text-white rounded-xl font-semibold hover:opacity-90 transition-all duration-200 shadow-lg transform hover:scale-105"
              >
                <FileText className="w-5 h-5" />
                Generate Summary
              </button>
            )}
          </div>
       

        
        </div>

        {/* Document Upload Section */}
        <div className="border-t border-gray-200 pt-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-700">
              Attach Supporting Documents
            </h3>
            <button
              onClick={onToggleDocumentUpload}
              className="inline-flex items-center gap-2 px-4 py-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
            >
              <Paperclip className="w-4 h-4" />
              {showDocumentUpload ? 'Hide Upload' : 'Add Documents'}
            </button>
          </div>
          
          {showDocumentUpload && (
            <DocumentUpload
              questionId={currentQuestion.id}
              onDocumentUpload={onDocumentUpload}
              uploadedDocuments={uploadedDocuments}
              onDocumentRemove={onDocumentRemove}
            />
          )}
          
          {/* Show document count if any are uploaded */}
          {uploadedDocuments[currentQuestion.id]?.length > 0 && !showDocumentUpload && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-blue-800 text-sm">
                📎 {uploadedDocuments[currentQuestion.id].length} document(s) attached to this question
              </p>
            </div>
          )}
        </div>

    
      </div>
    </div>
  );
};

export default QuestionDisplay;